export function downloadFileFromResponse(
    payload: unknown,
    fallbackFileName: string,
    mimeType: string,
) {
    const fileName = extractFileName(payload, fallbackFileName);

    if (payload instanceof Blob) {
        triggerDownload(payload, fileName);
        return;
    }

    if (payload instanceof ArrayBuffer) {
        triggerDownload(new Blob([payload], { type: mimeType }), fileName);
        return;
    }

    if (ArrayBuffer.isView(payload)) {
        triggerDownload(new Blob([viewToArrayBuffer(payload)], { type: mimeType }), fileName);
        return;
    }

    const base64 = extractBase64(payload);

    if (base64 === null) {
        throw new Error("Unsupported download payload received from backend");
    }

    const byteArray = base64ToUint8Array(stripBase64Prefix(base64));

    decompressIfNeeded(byteArray)
        .then((resolvedBytes) => triggerDownload(new Blob([toArrayBuffer(resolvedBytes)], { type: mimeType }), fileName))
        .catch(() => triggerDownload(new Blob([toArrayBuffer(byteArray)], { type: mimeType }), fileName));
}

function extractBase64(payload: unknown): string | null {
    if (typeof payload === "string") {
        return normalizeBase64String(payload);
    }

    if (payload && typeof payload === "object") {
        const candidatePayload = payload as Record<string, unknown>;
        const candidateKeys = ["base64", "data", "content", "result", "file", "payload"];

        for (const key of candidateKeys) {
            const value = candidatePayload[key];
            if (typeof value === "string" && value.length > 0) {
                return normalizeBase64String(value);
            }
        }
    }

    return null;
}

function extractFileName(payload: unknown, fallbackFileName: string): string {
    if (payload && typeof payload === "object") {
        const candidatePayload = payload as Record<string, unknown>;
        const fileName = candidatePayload.fileName;

        if (typeof fileName === "string" && fileName.length > 0) {
            return fileName;
        }
    }

    return fallbackFileName;
}

function stripBase64Prefix(value: string): string {
    const commaIndex = value.indexOf(",");

    if (value.startsWith("data:") && commaIndex >= 0) {
        return value.slice(commaIndex + 1);
    }

    return value;
}

function normalizeBase64String(value: string): string {
    const trimmed = value.trim();

    if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return trimmed.slice(1, -1).trim();
    }

    return trimmed;
}

function base64ToUint8Array(base64: string): Uint8Array {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let index = 0; index < byteCharacters.length; index++) {
        byteNumbers[index] = byteCharacters.charCodeAt(index);
    }

    return new Uint8Array(byteNumbers);
}

async function decompressIfNeeded(bytes: Uint8Array): Promise<Uint8Array> {
    if (!isGzip(bytes)) {
        return bytes;
    }

    const DecompressionStreamCtor = (globalThis as typeof globalThis & {
        DecompressionStream?: new (format: string) => TransformStream<Uint8Array, Uint8Array>
    }).DecompressionStream;

    if (typeof DecompressionStreamCtor === "undefined") {
        throw new Error("Gzip payload received but DecompressionStream is not available");
    }

    const decompressedStream = new Blob([toArrayBuffer(bytes)])
        .stream()
        .pipeThrough(new DecompressionStreamCtor("gzip"));

    return new Uint8Array(await new Response(decompressedStream).arrayBuffer());
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function viewToArrayBuffer(view: ArrayBufferView): ArrayBuffer {
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

function isGzip(bytes: Uint8Array): boolean {
    return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

function triggerDownload(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}